import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  NumberInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useVendorsAdmin,
  useCreateVendor,
  useUpdateVendor,
  useToggleVendorActive,
} from "../../../hooks/admin/useVendors";
import { Vendor } from "../../../services/admin/vendors.service";

const ITEMS_PER_PAGE = 20;

export default function VendorsAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  const [editing, setEditing] = useState<Vendor | null>(null);
  const [itemToToggle, setItemToToggle] = useState<Vendor | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const { data: vendors = [], isLoading, refetch } = useVendorsAdmin();
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();
  const toggleVendorActive = useToggleVendorActive();

  const form = useForm({
    initialValues: {
      name: "",
      address: "",
      email: "",
      primary_phone: "",
      secondary_phone: "",
      ihc_daily_minimum: 0,
    },
    validate: {
      name: (v: string) => (v.trim() ? null : "Name is required"),
    },
  });

  const filteredData = useMemo(() => {
    if (!debouncedSearch) return vendors;
    const q = debouncedSearch.toLowerCase();
    return vendors.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        v.email?.toLowerCase().includes(q) ||
        v.primaryPhone?.toLowerCase().includes(q) ||
        v.address?.toLowerCase().includes(q)
    );
  }, [vendors, debouncedSearch]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const handleAdd = () => {
    setEditing(null);
    form.reset();
    openFormModal();
  };

  const handleEdit = (item: Vendor) => {
    setEditing(item);
    form.setValues({
      name: item.name,
      address: item.address || "",
      email: item.email || "",
      primary_phone: item.primaryPhone || "",
      secondary_phone: item.secondaryPhone || "",
      ihc_daily_minimum: item.ihcDailyMinimum,
    });
    openFormModal();
  };

  const handleSubmit = async (values: typeof form.values) => {
    const data = {
      ...values,
      address: values.address || undefined,
      email: values.email || undefined,
      primary_phone: values.primary_phone || undefined,
      secondary_phone: values.secondary_phone || undefined,
    };

    if (editing) {
      await updateVendor.mutateAsync({ id: editing.id, ...data });
    } else {
      await createVendor.mutateAsync(data);
    }
    closeFormModal();
    form.reset();
  };

  const handleToggle = (item: Vendor) => {
    setItemToToggle(item);
    openConfirmModal();
  };

  const confirmToggle = async () => {
    if (itemToToggle) {
      await toggleVendorActive.mutateAsync(itemToToggle.id);
      closeConfirmModal();
      setItemToToggle(null);
    }
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Vendors</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage external IHC staining vendors and their daily minimum
                requirements
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search */}
          <Paper p="md" withBorder>
            <Group>
              <TextInput
                placeholder="Search by name, email, phone, or address..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1 }}
              />
              <Button
                variant="subtle"
                onClick={() => refetch()}
                loading={isLoading}
                leftSection={<IconRefresh size={16} />}
              >
                Refresh
              </Button>
            </Group>
          </Paper>

          {/* Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Vendors ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search
                    ? "No vendors match the current search"
                    : "No vendors found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Primary Phone</Table.Th>
                      <Table.Th>Address</Table.Th>
                      <Table.Th>IHC Daily Min</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {item.email || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {item.primaryPhone || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>
                            {item.address || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="blue" variant="light">
                            {item.ihcDailyMinimum}
                          </Badge>
                        </Table.Td>
                        <Table.Td style={{ width: 100 }}>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                            size="sm"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td style={{ width: 100 }}>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggle(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editing ? "Edit Vendor" : "Add New Vendor"}
          size="md"
          closeOnClickOutside={false}
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Name"
                required
                withAsterisk
                {...form.getInputProps("name")}
              />
              <TextInput label="Address" {...form.getInputProps("address")} />
              <TextInput
                label="Email"
                type="email"
                {...form.getInputProps("email")}
              />
              <TextInput
                label="Primary Phone"
                {...form.getInputProps("primary_phone")}
              />
              <TextInput
                label="Secondary Phone"
                {...form.getInputProps("secondary_phone")}
              />
              <NumberInput
                label="IHC Daily Minimum"
                min={0}
                {...form.getInputProps("ihc_daily_minimum")}
              />

              <Group justify="flex-end" mt="md" gap="xs">
                <Button variant="default" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createVendor.isPending || updateVendor.isPending}
                >
                  {editing ? "Update" : "Create"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            itemToToggle?.isActive ? "Deactivate Vendor" : "Activate Vendor"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"} the vendor{" "}
              <strong>{itemToToggle?.name}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated vendors will no longer be available for selection.
              </Text>
            )}
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggle}
                loading={toggleVendorActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
